import { useOnlineStatus } from "@/hooks/useOnlineStatus";
import { WifiOff, Wifi, CloudOff, RefreshCw, AlertCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { useEffect, useState } from "react";

export function OfflineIndicator() {
  const { isOnline, wasOffline, isOffline, pendingCount, connectionQuality } = useOnlineStatus();
  const [showReconnected, setShowReconnected] = useState(false);

  // Show "Reconnected" message briefly when coming back online
  useEffect(() => {
    if (wasOffline && isOnline) {
      setShowReconnected(true);
      const timer = setTimeout(() => setShowReconnected(false), 5000);
      return () => clearTimeout(timer);
    }
  }, [wasOffline, isOnline]);

  // Don't show anything if online and no pending operations and good connection
  if (isOnline && !showReconnected && pendingCount === 0 && connectionQuality === 'good') {
    return null;
  }

  return (
    <div className="fixed bottom-20 md:bottom-4 left-1/2 -translate-x-1/2 z-50 animate-in fade-in slide-in-from-bottom-4 duration-300">
      {/* Offline Banner */}
      {isOffline && (
        <div className="flex items-center gap-3 bg-destructive text-destructive-foreground px-4 py-3 rounded-full shadow-lg">
          <WifiOff className="h-5 w-5 animate-pulse" />
          <span className="font-medium">You're offline</span>
          {pendingCount > 0 && (
            <Badge variant="secondary" className="bg-background/20 text-destructive-foreground">
              {pendingCount} pending
            </Badge>
          )}
        </div>
      )}

      {/* Reconnected Banner */}
      {showReconnected && isOnline && (
        <div className="flex items-center gap-3 bg-green-500 text-white px-4 py-3 rounded-full shadow-lg">
          <Wifi className="h-5 w-5" />
          <span className="font-medium">Back online!</span>
          {pendingCount > 0 && (
            <div className="flex items-center gap-1.5">
              <RefreshCw className="h-4 w-4 animate-spin" />
              <span className="text-sm">Syncing {pendingCount}...</span>
            </div>
          )}
        </div>
      )}

      {/* Slow Connection Warning */}
      {isOnline && !showReconnected && connectionQuality === 'slow' && (
        <div className="flex items-center gap-3 bg-yellow-500 text-yellow-950 px-4 py-3 rounded-full shadow-lg">
          <AlertCircle className="h-5 w-5" />
          <span className="font-medium">Slow connection</span>
        </div>
      )}

      {/* Poor Connection Warning */}
      {isOnline && !showReconnected && connectionQuality === 'poor' && (
        <div className="flex items-center gap-3 bg-orange-500 text-white px-4 py-3 rounded-full shadow-lg">
          <CloudOff className="h-5 w-5" />
          <span className="font-medium">Unstable connection</span>
        </div>
      )}

      {/* Pending Operations (when online but have pending) */}
      {isOnline && !showReconnected && pendingCount > 0 && connectionQuality === 'good' && (
        <div className="flex items-center gap-3 bg-primary text-primary-foreground px-4 py-3 rounded-full shadow-lg">
          <RefreshCw className="h-5 w-5 animate-spin" />
          <span className="font-medium">Syncing {pendingCount} operations...</span>
        </div>
      )}
    </div>
  );
}
